/**
 * Generic Flight Selector component
 *
 *
 * TODO:
 * 1. Fill out serviceTypes from backend Call getServiceTypes
 * 2. Operators should always be bound to operatorList.
 *    Just get the first element if it is TextBox (unrestricted)
 * 3. Hidden and Disable has a priority. So if for one of airports it is set "hideen" the it is hidden
 * 4. Primary/secondary fields: For now, just split and order to mimic first and second row of current OCS View Flights page (ignoring the airport season selector fields, who will lead first row)
 * 5. Operator dropdown must be initially hidden (by static configuration)

 */

import React, { Fragment, useEffect, useState, useMemo } from "react"
import { CSSTransition } from "react-transition-group"

import _ from "lodash"

import Card from "./generic/Card"

import {
  IFieldValues,
  updateAirportSeasonsSelection,
  updateCollapsedCardsSelection
} from "../containers/selectionSlice"

import {
  AircraftRegistrationSearchField,
  AircraftTypeField,
  DOOPField,
  OperatorField,
  OperatorDropdownField,
  FlightModeField,
  ClearCodeField,
  StationCountryField,
  ServiceTypeDropdownField,
  SlotIdField,
  HandlerField,
  TerminalDropdownField,
  ParkingLocationField
  // NoiseCertificateField
} from "./selectorFields"

import { ValueLabel } from "./generic/fields"

import AirportSeasonSelector from "./AirportSeasonSelector"
import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"

import SeasonDateRange from "./SeasonDateRange"
import ServiceNumberRange from "./ServiceNumberRange"
import { getFlightSelectorConfiguration, subtractYears } from "./Utils"

import TimeRangeField from "./generic/form/TimeRangeField"
import { RadioBooleanField } from "./generic/form/RadioBooleanField"
import { CheckBooleanField } from "./generic/form/CheckBooleanField"
import {
  collapsedCardName,
  CommonText,
  FieldName,
  FlightText,
  FormText
} from "../common/text"

import { IAirportseasonSelectorConfig, IFieldConfig } from "../pages/Config"

import SeasonDatePicker from "./SeasonDatePicker"
import { CodeType } from "./AirportSelector"
import { HandlingApp, PageId } from "../common/pageId"
import Button from "./generic/buttons/Button"
import { HistoryModal } from "./generic/HistoryModal"
import { useFlightSelectorInitValues } from "../hooks/useFlighSelectorInitValues"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import { PageGroup } from "../common/transaction"
import DropdownField, { MenuPlacement } from "./generic/form/DropdownField"
import { PPRIdRegex, slotIdRegex } from "../common/regex"
import usePrevious from "../hooks/usePrevious"
import DatePickerField from "./generic/form/DatePickerField"

export interface IFlightSelectorValues {
  [FieldName.flightRule]: "VFR" | "IFR" | string | string[]
  [FieldName.operators]: string[]
  [FieldName.excludeOper]: boolean
  [FieldName.doop]: string
  [FieldName.slotId]: string
  [FieldName.aircraftType]: string
  [FieldName.aircraftRegistration]: string
  [FieldName.startDate]: string
  [FieldName.endDate]: string
  [FieldName.flightMode]: string
  [FieldName.clearCode]: string
  [FieldName.clearedTimeFrom]: string
  [FieldName.clearedTimeTo]: string
  [FieldName.requiredTimeFrom]: string
  [FieldName.requiredTimeTo]: string
  [FieldName.origDest]: string
  [FieldName.prevNext]: string
  [FieldName.terminal]: string
  [FieldName.serviceNumber]: string
  [FieldName.serviceNumberTo]?: string
  [FieldName.serviceTypes]: string[]
  [FieldName.delink]: boolean
  [FieldName.normalizePeriod]: boolean
  [FieldName.waitlist]: boolean
  [FieldName.excludeNoslots]: boolean
  [FieldName.noslotsOnly]: boolean
  [FieldName.lastUpdate]: string
  [FieldName.canxSinceDate]: string
  [FieldName.viewRestricted]: boolean
  [FieldName.useOwnOperators]: boolean
  [FieldName.viewSubmittedShl]: boolean
  [FieldName.handler]: string
  [FieldName.vfr]?: string
  [FieldName.towbar]: "T" | "F" | string
  [FieldName.parkingLocation]: string
  [FieldName.noiseCertificate]: string
}

export interface IMainFlightSelectorProps {
  airportSeasons: AirportSeasonConfigurations
  handleChange?: (field: string, val: any, error?: boolean) => void
  handleAvailChange?: (field: string, val: any, error?: boolean) => void
  airportseasonSelectorConfig?: IAirportseasonSelectorConfig
  hasError?: boolean
  errors?: any
  useOwnOperatorError?: boolean
  errorMsg?: string
  touched?: boolean
  invalidDate?: boolean
  emptyMatrix?: () => void
}

interface IFlightSelectorProps extends IMainFlightSelectorProps {
  title: string
  onFieldChange: (field: string, value: any, error?: boolean) => void
  onFlightSelectorChange: (
    newFlightSelectorValues: IFlightSelectorValues
  ) => void
  values: IFlightSelectorValues
  pageId?: string
  codeType: CodeType
  flightSelectorConfig?: IFieldConfig
  serviceNumberAsRange?: boolean
  config?: any
  titleSize?: "sm" | "lg"
  pageGroup?: PageGroup
  fieldNameErrors?: any
}

const FlightSelector: React.FunctionComponent<IFlightSelectorProps> = ({
  airportSeasons,
  onFieldChange,
  handleChange,
  onFlightSelectorChange,
  values,
  pageId,
  codeType,
  flightSelectorConfig,
  airportseasonSelectorConfig,
  hasError,
  useOwnOperatorError,
  invalidDate,
  errorMsg,
  touched,
  title,
  titleSize,
  errors,
  fieldNameErrors
}) => {
  const dispatch = useAppDispatch()
  const defaultServiceTypes = useAppSelector(
    (state) => state["appReducer"].serviceTypes
  )

  const { versionInfo } = useAppSelector((state) => state["appReducer"])

  const { season, countries, airports, useLocalTime } =
    useAppSelector(
      (state) => state["selectionReducer"].airportSeasonsSelector
    ) || {}

  const collapsedCards = useAppSelector(
    (state) => state["selectionReducer"].collapsedCards
  )
  const handlerOpts = airportSeasons[0]?.handlers.map((handler) => ({
    value: handler,
    label: handler
  }))

  const cardFieldsMap = {
    [collapsedCardName.flightID]: [
      FieldName.operators,
      FieldName.serviceNumber,
      FieldName.aircraftRegistration
    ],
    [collapsedCardName.period]: [
      FieldName.startDate,
      FieldName.endDate,
      FieldName.normalizePeriod,
      FieldName.doop,
      FieldName.delink,
      FieldName.flightMode
    ],
    [collapsedCardName.times]: [
      FieldName.clearCode,
      FieldName.clearedTimeTo,
      FieldName.clearedTimeFrom,
      FieldName.requiredTimeFrom,
      FieldName.clearedTimeTo,
      FieldName.waitlist,
      FieldName.excludeNoslots,
      FieldName.noslotsOnly
    ],
    [collapsedCardName.other]: [
      FieldName.origDest,
      FieldName.prevNext,
      FieldName.aircraftType,
      FieldName.terminal,
      FieldName.serviceTypes,
      FieldName.lastUpdate,
      FieldName.canxSinceDate,
      FieldName.viewSubmittedShl
    ],
    [collapsedCardName.handling]: [
      FieldName.handler,
      FieldName.flightRule,
      FieldName.towbar,
      FieldName.parkingLocation
      // FieldName.noiseCertificate
    ]
  }

  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false)
  const FlightSelectorInitValues = useFlightSelectorInitValues()
  const prevAirports = usePrevious(airports)

  const isSingleDate = pageId === PageId.EditFlightsSD
  const isPPR = versionInfo.handlingApplication === HandlingApp.FRAPORTGR

  const showField = (field: FieldName): boolean =>
    excludeFields ? !excludeFields[field] : true
  const showCard = (fields: FieldName[]): boolean => {
    const showFields = fields.filter((field) => showField(field))

    return showFields.length > 0
  }
  const showHandlingCard = (fields: FieldName[]): boolean => {
    const showFields = fields.filter((field) => showField(field))

    if (
      showFields.length === 1 &&
      showFields.includes(FieldName.parkingLocation)
    ) {
      return parkingLocationOpts.length > 0
    }

    return showFields.length > 0
  }

  const handleInvalidCards = () => {
    if (touched) {
      fieldNameErrors &&
        fieldNameErrors.forEach((error: string) =>
          dispatch(
            updateCollapsedCardsSelection({
              field: collapsedCardName[error],
              value: false
            })
          )
        )
    }
  }

  useEffect(() => {
    handleInvalidCards()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched])

  const handleCollapsedCardsWithValues = (
    newValues?: IFlightSelectorValues
  ) => {
    const _values = newValues ? newValues : _.cloneDeep(values)

    Object.keys(_values).forEach((field) => {
      if (
        showField(FieldName[field]) &&
        !_.isEqual(FlightSelectorInitValues[field], _values[field])
      ) {
        const cardName = Object.keys(cardFieldsMap).find((card) =>
          cardFieldsMap[card].includes(field)
        )

        if (collapsedCards[collapsedCardName[cardName]]) {
          handleCollapseCards(collapsedCardName[cardName])
        }
      }
    })
  }

  useEffect(() => {
    handleCollapsedCardsWithValues()
    // eslint-disable-next-line
  }, [])

  const handleFieldChange = (name: string, value: any, error?: boolean) => {
    if (typeof onFieldChange === "function") {
      onFieldChange(name, value, error)
      setClearErrors(true)
    }
  }

  useEffect(() => {
    if (excludeFields?.endDate) {
      handleFieldChange("endDate", values.startDate, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.startDate])

  // get configuration for selected season and airports
  const config = getFlightSelectorConfiguration(
    airportSeasons,
    season,
    airports.length > 0
      ? airports
      : countries.length > 0
      ? airportSeasons
          .filter(
            (as) =>
              as.season === season &&
              countries.indexOf(as.country) >= 0 &&
              (useLocalTime ? as.supportsLt : as.supportsUtc)
          )
          .map((a) => a.airport)
      : [],
    codeType
  )

  //update viewRestricted if config is operatorRestricted or useOwnOperators is chosen
  useEffect(() => {
    let updViewRestricted: boolean =
      (config && config.operatorRestricted) || values.useOwnOperators
        ? true
        : false

    if (updViewRestricted !== values.viewRestricted) {
      onFieldChange("viewRestricted", updViewRestricted)
    }
  }, [config, values.useOwnOperators, values.viewRestricted, onFieldChange])

  //update operators if restricted changes
  useEffect(() => {
    let operatorRestricted: boolean =
      (config && config.operatorRestricted) || values.useOwnOperators
        ? true
        : false
    const operators = _.cloneDeep(values.operators)

    const updOperators = operators
      ? operatorRestricted
        ? operators.filter((val) =>
            config ? config.operators.find((o) => val === o) : false
          )
        : operators.length > 1
        ? operators.splice(0, 1)
        : null
      : null

    if (updOperators && !_.isEqual(values.operators, updOperators)) {
      onFieldChange("operators", updOperators)
    }
  }, [config, values.useOwnOperators, values.operators, onFieldChange])

  useEffect(() => {}, [airportSeasons, season, countries, airports, values])
  const excludeFields = flightSelectorConfig
  const serviceNumberAsRange = !excludeFields?.serviceNumberTo

  useEffect(() => {
    if (!serviceNumberAsRange) {
      handleFieldChange("serviceNumberTo", "")
    }
    // eslint-disable-next-line
  }, [serviceNumberAsRange])

  const isOperatorRestricted = config && config.operatorRestricted
  const operatorsArr = config && config.operators
  const filtredOperationArr =
    operatorsArr && operatorsArr.filter((opr) => opr !== "")

  const unlimitedOperators =
    config && config.operators.indexOf("") >= 0 ? true : false
  const showUseOwnOperators =
    !isOperatorRestricted &&
    !unlimitedOperators &&
    !excludeFields?.useOwnOperators &&
    filtredOperationArr &&
    filtredOperationArr.length > 0

  useEffect(() => {
    if (
      (isOperatorRestricted || unlimitedOperators) &&
      values.useOwnOperators
    ) {
      onFieldChange("useOwnOperators", false)
    }
  }, [
    config,
    values.useOwnOperators,
    isOperatorRestricted,
    unlimitedOperators,
    onFieldChange
  ])

  const handleCollapseCards = (cardType: collapsedCardName) => {
    dispatch(
      updateCollapsedCardsSelection({
        field: cardType,
        value: !collapsedCards[cardType]
      })
    )
  }

  const handleSetHistory = (chosenHistory: IFieldValues) => {
    let values = chosenHistory
    let updFlightSelectorState = _.cloneDeep(FlightSelectorInitValues)

    _.each(_.keys(values), (key) => {
      if (key === "airports") {
        dispatch(
          updateAirportSeasonsSelection({
            field: FieldName[key],
            value: values[key]
          })
        )
      } else {
        updFlightSelectorState[key] = values[key]
      }
    })

    onFlightSelectorChange(updFlightSelectorState)
    handleCollapsedCardsWithValues(updFlightSelectorState)
    setShowHistoryModal(false)
  }
  const [clearErrors, setClearErrors] = useState(true)
  const handleClearSelection = () => {
    onFlightSelectorChange(FlightSelectorInitValues)
    setClearErrors(false)
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.airports,
        value: []
      })
    )
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.countries,
        value: []
      })
    )
  }

  const terminalOpts = useMemo(() => {
    return config && config.terminals
      ? _.map(config.terminals, (o) => {
          return { value: o, label: o }
        })
      : []
  }, [config])

  useEffect(() => {
    if (
      (config &&
        values.terminal !== "" &&
        prevAirports !== undefined &&
        !config.terminals.includes(values.terminal)) ||
      airports.length === 0
    ) {
      onFieldChange("terminal", "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airports])

  const parkingLocationOpts = useMemo(() => {
    return config && config.parkingLocations
      ? _.map(config.parkingLocations, (o) => {
          return { value: o, label: o }
        })
      : []
  }, [config])

  useEffect(() => {
    if (
      values.parkingLocation !== "" &&
      !config?.parkingLocations.includes(values.parkingLocation)
    ) {
      onFieldChange("parkingLocation", "")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkingLocationOpts])

  const getOperator = () => {
    const opts =
      config && (config.operatorRestricted || values.useOwnOperators)
        ? _.map(config.operators, (o) => {
            return { value: o, label: o }
          })
        : []

    return (
      <Fragment>
        {showUseOwnOperators && (
          <div className="ocs-mr-12">
            <RadioBooleanField
              caption="Show Operators"
              name={FieldName.useOwnOperators}
              value={values.useOwnOperators}
              options={[
                { label: "All", value: false },
                { label: "Own only", value: true }
              ]}
              onChange={handleFieldChange}
            />
          </div>
        )}
        {!excludeFields?.excludeOper && (
          <div className="ocs-mr-12">
            <RadioBooleanField
              name={FieldName.excludeOper}
              value={values.excludeOper}
              options={[
                { label: "Include", value: false },
                { label: "Exclude", value: true }
              ]}
              onChange={handleFieldChange}
            />
          </div>
        )}
        <div className="ocs-w-96 ocs-mr-12">
          {values.useOwnOperators || isOperatorRestricted ? (
            <OperatorDropdownField
              isMulti
              value={values.operators}
              options={opts}
              clearable={false}
              onChange={handleFieldChange}
              useOwnOperatorError={useOwnOperatorError}
              errorMsg={errorMsg}
              menuPortalTarget={document.body}
            />
          ) : (
            <OperatorField
              value={values.operators && values.operators[0]}
              clearErrors={clearErrors}
              onChange={(name, val, error) => {
                if (typeof val === "string") {
                  val = val.trim()
                }
                handleFieldChange(name, val !== "" && val ? [val] : [], error)
              }}
            />
          )}
        </div>
      </Fragment>
    )
  }

  const getServiceTypes = () => {
    let opts: ValueLabel[]

    if (
      config &&
      config.serviceTypes &&
      config.serviceTypes.length > 0 &&
      !(config.serviceTypes.length === 1 && config.serviceTypes[0] === "")
    ) {
      opts = _.map(config.serviceTypes, (o) => {
        return { value: o, label: o }
      })
    } else {
      opts = _.map(defaultServiceTypes, (o) => {
        return { value: o.code, label: o.code + " " + o.description }
      })
    }
    return (
      <div className="ocs-w-100 ocs-mr-12">
        <ServiceTypeDropdownField
          isMulti
          value={values.serviceTypes}
          options={opts}
          onChange={handleFieldChange}
          formatOptionLabel={(opt, { context }) => {
            return context === "menu" ? opt.label : opt.value
          }}
          menuPortalTarget={document.body}
          menuPlacement={MenuPlacement.top}
        />
      </div>
    )
  }

  return (
    <Fragment>
      <div className="ocs-flight-selector-container">
        <div className="ocs-selector-container-heading d-flex align-items-center justify-content-between">
          <h2 className={`ocs-heading ${titleSize ? titleSize : ""}`}>
            {title}
          </h2>
          <div className="d-flex mq-flex-direction-row">
            <div className="btn-margin">
              <Button
                onClick={() => setShowHistoryModal(true)}
                type="button"
                panelBtn
                className="mq-btn-height"
              >
                {FormText.searchHistory}
              </Button>
            </div>
            <Button
              onClick={handleClearSelection}
              type="button"
              panelBtn
              className="mq-btn-height"
            >
              {CommonText.clear}
            </Button>
          </div>
        </div>
        <Card title={FlightText.airportSeson}>
          <fieldset>
            <AirportSeasonSelector
              airportseasonSelectorConfig={airportseasonSelectorConfig}
              airportSeasons={airportSeasons}
              handleChange={handleChange}
              codeType={codeType}
              hasError={hasError}
              errorMsg={errorMsg}
            />
          </fieldset>
        </Card>
        {showCard(cardFieldsMap[collapsedCardName.flightID]) && (
          <Card
            title={FlightText.flightId}
            onClick={() => handleCollapseCards(collapsedCardName.flightID)}
            collapsed={collapsedCards.flightID}
          >
            <CSSTransition
              classNames="card-collapse"
              timeout={300}
              in={!collapsedCards.flightID}
              unmountOnExit
            >
              <div className="d-flex flex-column">
                <fieldset className="d-flex flex-wrap row-gap-12 ocs-mb-12">
                  {getOperator()}
                  {showField(FieldName.serviceNumber) && (
                    <div
                      className={
                        serviceNumberAsRange ? "ocs-mr-4" : "ocs-mr-12"
                      }
                    >
                      <ServiceNumberRange
                        serviceNumber={values.serviceNumber}
                        onChange={handleFieldChange}
                        serviceNumberTo={values.serviceNumberTo}
                        showAsSingle={!serviceNumberAsRange}
                        clearErrors={clearErrors}
                      />
                    </div>
                  )}
                  {showField(FieldName.aircraftRegistration) && (
                    <div className="ocs-w-92">
                      <AircraftRegistrationSearchField
                        clearErrors={clearErrors}
                        value={values.aircraftRegistration}
                        onChange={handleFieldChange}
                      />
                    </div>
                  )}
                  {showField(FieldName.slotId) && (
                    <div className={isPPR ? "ocs-w-340" : ""}>
                      <SlotIdField
                        name="slotId"
                        value={values.slotId}
                        onChange={handleFieldChange}
                        caption={isPPR ? FlightText.pprId : FlightText.slotId}
                        validator={isPPR ? PPRIdRegex : slotIdRegex}
                      />
                    </div>
                  )}
                </fieldset>
              </div>
            </CSSTransition>
          </Card>
        )}

        {showCard(cardFieldsMap[collapsedCardName.period]) && (
          <Card
            title={FlightText.period}
            onClick={() => handleCollapseCards(collapsedCardName.period)}
            collapsed={collapsedCards.period}
          >
            <div className="d-flex justify-content-end align-items-center" />
            <CSSTransition
              classNames="card-collapse"
              timeout={300}
              in={!collapsedCards.period}
              unmountOnExit
            >
              <div className="d-flex flex-column">
                <fieldset className="d-flex ocs-mb-12">
                  {(showField(FieldName.startDate) ||
                    showField(FieldName.endDate)) && (
                    <div
                      className={`${
                        !isSingleDate
                          ? "ocs-mr-4 ocs-w-252"
                          : "ocs-mr-12 ocs-w-252"
                      }`}
                    >
                      {showField(FieldName.endDate) ? (
                        <SeasonDateRange
                          dateRangeStartValue={values.startDate}
                          dateRangeEndValue={values.endDate}
                          errors={errors}
                          onChange={handleFieldChange}
                        />
                      ) : (
                        <div className={`${isSingleDate ? "ocs-w-84" : ""}`}>
                          <SeasonDatePicker
                            caption={FlightText.date}
                            name={FieldName.startDate}
                            value={values.startDate}
                            onChange={handleFieldChange}
                            required
                            touched={touched}
                            isEmpty={values.startDate === null || invalidDate}
                          />
                        </div>
                      )}
                      {showField(FieldName.normalizePeriod) && (
                        <div className="ocs-mt-12 ">
                          <CheckBooleanField
                            name={FieldName.normalizePeriod}
                            value={values.normalizePeriod ?? false}
                            caption={
                              isSingleDate
                                ? FlightText.disableOvernightDeps
                                : FlightText.normalizePeriod
                            }
                            onChange={handleFieldChange}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {showField(FieldName.doop) && (
                    <div className="ocs-w-92 ocs-mr-12">
                      <DOOPField
                        value={values.doop}
                        onChange={handleFieldChange}
                        clearErrors={clearErrors}
                      />
                    </div>
                  )}
                  <div className="ocs-w-72">
                    {showField(FieldName.flightMode) && (
                      <FlightModeField
                        value={values.flightMode}
                        onChange={handleFieldChange}
                        menuPortalTarget={document.body}
                      />
                    )}
                    {showField(FieldName.delink) && (
                      <div className="ocs-mt-12 ">
                        <div>
                          <CheckBooleanField
                            name={FieldName.delink}
                            value={values.delink ?? false}
                            caption={FlightText.delink}
                            onChange={handleFieldChange}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </fieldset>
              </div>
            </CSSTransition>
          </Card>
        )}
        {showCard(cardFieldsMap[collapsedCardName.times]) && (
          <Card
            title={FlightText.times}
            onClick={() => handleCollapseCards(collapsedCardName.times)}
            collapsed={collapsedCards.times}
          >
            <CSSTransition
              classNames="card-collapse"
              timeout={300}
              in={!collapsedCards.times}
              unmountOnExit
            >
              <fieldset className="d-flex flex-column">
                <div className="d-flex ocs-mb-12">
                  {showField(FieldName.clearCode) && (
                    <div className="ocs-w-80 ocs-mr-12">
                      <ClearCodeField
                        value={values.clearCode}
                        onChange={handleFieldChange}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  )}
                  {showField(FieldName.clearedTimeTo) &&
                    showField(FieldName.clearedTimeTo) && (
                      <div className="ocs-mr-4 ocs-w-128">
                        <TimeRangeField
                          caption={FlightText.clearTimeRange}
                          fromName={FieldName.clearedTimeFrom}
                          fromValue={values.clearedTimeFrom}
                          toName={FieldName.clearedTimeTo}
                          toValue={values.clearedTimeTo}
                          onChange={handleFieldChange}
                          hasError={errors?.clearedTimeError}
                          clearErrors={clearErrors}
                        />
                      </div>
                    )}
                  {showField(FieldName.requiredTimeFrom) &&
                    showField(FieldName.requiredTimeTo) && (
                      <div className="ocs-w-128">
                        <TimeRangeField
                          caption={FlightText.requiredTimeRange}
                          fromName={FieldName.requiredTimeFrom}
                          fromValue={values.requiredTimeFrom}
                          toName={FieldName.requiredTimeTo}
                          toValue={values.requiredTimeTo}
                          onChange={handleFieldChange}
                          hasError={errors?.requiredTimeError}
                          clearErrors={clearErrors}
                        />
                      </div>
                    )}
                </div>
                <div className="d-flex ocs-mb-12">
                  {showField(FieldName.waitlist) && (
                    <div className="ocs-mr-12">
                      <CheckBooleanField
                        name={FieldName.waitlist}
                        value={values.waitlist ?? false}
                        caption={FlightText.waitlistOnly}
                        onChange={handleFieldChange}
                      />
                    </div>
                  )}
                  {showField(FieldName.excludeNoslots) && (
                    <div className="ocs-mr-12">
                      <CheckBooleanField
                        name={FieldName.excludeNoslots}
                        value={values.excludeNoslots ?? false}
                        caption={FlightText.excludeSlotsFlights}
                        onChange={handleFieldChange}
                      />
                    </div>
                  )}

                  {showField(FieldName.noslotsOnly) && (
                    <div>
                      <CheckBooleanField
                        name={FieldName.noslotsOnly}
                        value={values.noslotsOnly ?? false}
                        caption={FlightText.noSlotFlightIOnly}
                        onChange={handleFieldChange}
                      />
                    </div>
                  )}
                </div>
              </fieldset>
            </CSSTransition>
          </Card>
        )}
        {showCard(cardFieldsMap[collapsedCardName.other]) && (
          <Card
            title={FlightText.other}
            onClick={() => handleCollapseCards(collapsedCardName.other)}
            collapsed={collapsedCards.other}
          >
            <CSSTransition
              classNames="card-collapse"
              timeout={300}
              in={!collapsedCards.other}
              unmountOnExit
            >
              <fieldset className="d-flex flex-column">
                <div className="d-flex ocs-mb-12 mq-flex-direction-row">
                  {showField(FieldName.origDest) && (
                    <div className="ocs-w-80 ocs-mr-12">
                      <StationCountryField
                        name={FieldName.origDest}
                        caption={FlightText.origDest}
                        value={values.origDest}
                        codeType={codeType}
                        onChange={handleFieldChange}
                        clearErrors={clearErrors}
                      />
                    </div>
                  )}
                  {showField(FieldName.prevNext) && (
                    <div className="ocs-mr-12 ocs-w-80">
                      <StationCountryField
                        name={FieldName.prevNext}
                        caption={CommonText.lastNext}
                        value={values.prevNext}
                        codeType={codeType}
                        onChange={handleFieldChange}
                        clearErrors={clearErrors}
                      />
                    </div>
                  )}
                  {showField(FieldName.aircraftType) && (
                    <div className="ocs-w-80">
                      <AircraftTypeField
                        value={values.aircraftType}
                        onChange={handleFieldChange}
                        codeType={codeType}
                        clearErrors={clearErrors}
                      />
                    </div>
                  )}
                </div>
                <div className="d-flex ocs-mb-12 mq-flex-direction-row">
                  {showField(FieldName.terminal) && (
                    <div className="ocs-w-180 ocs-mr-12 ">
                      <TerminalDropdownField
                        options={terminalOpts}
                        value={values.terminal}
                        onChange={handleFieldChange}
                        clearable
                        isCreatable
                        menuPortalTarget={document.body}
                      />
                    </div>
                  )}

                  {showField(FieldName.serviceTypes) && getServiceTypes()}

                  {showField(FieldName.lastUpdate) && (
                    <DatePickerField
                      formatDateVal
                      displayFormat="ddMMMyyyy"
                      min={subtractYears(2)}
                      caption={FlightText.lastUpdate}
                      name={FieldName.lastUpdate}
                      value={values.lastUpdate}
                      onChange={(name: string, value: string) => {
                        handleFieldChange(name, value)
                      }}
                    />
                  )}
                  {showField(FieldName.canxSinceDate) && (
                    <DatePickerField
                      formatDateVal
                      caption={FlightText.lastUpdateCanceled}
                      name={FieldName.canxSinceDate}
                      value={values.canxSinceDate}
                      displayFormat="ddMMMyyyy"
                      onChange={(name: string, value: string) => {
                        handleFieldChange(name, value)
                      }}
                    />
                  )}
                </div>
                {showField(FieldName.viewSubmittedShl) && (
                  <div className=" ocs-mb-12">
                    <CheckBooleanField
                      name={FieldName.viewSubmittedShl}
                      value={values.viewSubmittedShl}
                      caption={FlightText.viewSubmitted}
                      onChange={handleFieldChange}
                    />
                  </div>
                )}
              </fieldset>
            </CSSTransition>
          </Card>
        )}
        {showHandlingCard(cardFieldsMap[collapsedCardName.handling]) && (
          <Card
            title={FlightText.handling}
            onClick={() => handleCollapseCards(collapsedCardName.handling)}
            collapsed={collapsedCards.handling}
          >
            <CSSTransition
              classNames="card-collapse"
              timeout={300}
              in={!collapsedCards.handling}
              unmountOnExit
            >
              <fieldset className="d-flex flex-column">
                <div className="d-flex">
                  <div className="ocs-mr-12 ocs-w-220 ocs-mb-12">
                    {showField(FieldName.handler) && (
                      <HandlerField
                        value={values.handler}
                        onChange={handleFieldChange}
                        options={handlerOpts}
                        clearable
                      />
                    )}
                  </div>

                  <div className="d-flex ocs-mb-12">
                    {showField(FieldName.flightRule) && (
                      <div className="ocs-mr-12 ocs-w-84">
                        <DropdownField
                          caption={FlightText.vfrCaption}
                          name={FieldName.flightRule}
                          value={values.flightRule}
                          clearable
                          options={[
                            { value: "VFR", label: FlightText.vfr },
                            { value: "IFR", label: FlightText.ifr }
                          ]}
                          onChange={handleFieldChange}
                        />
                      </div>
                    )}
                  </div>

                  {showField(FieldName.towbar) && (
                    <div className="ocs-w-80">
                      <DropdownField
                        caption={FlightText.towbar}
                        name={FieldName.towbar}
                        value={values.towbar}
                        clearable
                        options={[
                          { value: "T", label: "Yes" },
                          { value: "F", label: "No" }
                        ]}
                        onChange={handleFieldChange}
                      />
                    </div>
                  )}
                </div>

                <div className="d-flex ocs-mb-12">
                  <div className="ocs-mr-12 ocs-w-240">
                    {showField(FieldName.parkingLocation) && (
                      <ParkingLocationField
                        value={values.parkingLocation}
                        onChange={handleFieldChange}
                        options={parkingLocationOpts}
                        clearable
                      />
                    )}
                  </div>
                  {/* <div className="ocs-w-120">
                      {showField(FieldName.noiseCertificate) && (
                        <NoiseCertificateField
                          value={values.noiseCertificate}
                          onChange={handleFieldChange}
                        />
                      )}
                    </div> */}
                </div>
              </fieldset>
            </CSSTransition>
          </Card>
        )}
      </div>
      {showHistoryModal && (
        <HistoryModal
          visible={showHistoryModal}
          hasActiveSearch={false}
          excludeFields={excludeFields}
          onClose={() => setShowHistoryModal(false)}
          onSelect={handleSetHistory}
        />
      )}
    </Fragment>
  )
}

export default FlightSelector
